import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
  useCallback,
} from "react";
import { api } from "../api/axios";

const CART_STORAGE_KEY = "cart";
const CartContext = createContext(null);

function loadInitialCart() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function cartReducer(state, action) {
  switch (action.type) {
    case "SET_ITEMS":
      return action.payload;

    case "ADD_ITEM": {
      const item = action.payload;
      const qtyToAdd = item.quantity ?? 1;

      const existing = state.find((x) => String(x.id) === String(item.id));

      if (existing) {
        return state.map((x) =>
          String(x.id) === String(item.id)
            ? { ...x, quantity: x.quantity + qtyToAdd }
            : x
        );
      }

      return [
        ...state,
        {
          id: item.id,
          product_id: item.product_id ?? null,
          name: item.name,
          price: Number(item.price) || 0,
          image_url: item.image_url || null,
          quantity: qtyToAdd,
          variant_id: item.variant_id ?? null,
          variant_label: item.variant_label ?? null,
          variant_type: item.variant_type ?? null,
          weight_kg: Number(item.weight_kg) || 0,
        },
      ];
    }

    case "DECREMENT":
      return state
        .map((x) =>
          String(x.id) === String(action.payload)
            ? { ...x, quantity: x.quantity - 1 }
            : x
        )
        .filter((x) => x.quantity > 0);

    case "REMOVE_ITEM":
      return state.filter((x) => String(x.id) !== String(action.payload));

    case "SET_QTY": {
      const { id, quantity } = action.payload;
      const q = Math.max(0, Number(quantity) || 0);

      if (q === 0) {
        return state.filter((x) => String(x.id) !== String(id));
      }

      return state.map((x) =>
        String(x.id) === String(id) ? { ...x, quantity: q } : x
      );
    }

    case "CLEAR":
      return [];    

    default:
      return state;
  }
}

function toApiItems(items) {
  return items.map((i) => ({
    product_variant_id: Number(i.variant_id ?? i.id),
    quantity: Number(i.quantity),
  }));
}

function fromApiItems(apiItems) {
  
  return apiItems.map((i) => ({
    id: i.product_variant_id,
    product_id: i.product_id ?? null,
    name: i.name ?? i.product_name ?? "",
    price: Number(i.price ?? i.unit_price) || 0,
    image_url: i.image_url ?? null,
    quantity: Number(i.quantity) || 0,
    variant_id: i.product_variant_id,
    variant_label: i.variant_label ?? null,
    variant_type: i.variant_type ?? null,
    weight_kg: Number(i.weight_kg) || 0,
  }));
}

function mergeItems(serverItems, localItems) {
  const map = new Map();

  for (const s of serverItems) {
    map.set(String(s.id), { ...s });
  }

  for (const l of localItems) {
    const key = String(l.id);

    if (map.has(key)) {
      map.set(key, {
        ...map.get(key),
        quantity: map.get(key).quantity + l.quantity,
      });
    } else {
      map.set(key, { ...l });
    }
  }

  return Array.from(map.values()).filter((x) => x.quantity > 0);
}

export function CartProvider({ children }) {
  const [items, dispatch] = useReducer(cartReducer, undefined, loadInitialCart);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const [subtotal, setSubtotal] = useState(0);
  const [totalWeightKg, setTotalWeightKg] = useState(0);
  const [shippingCost, setShippingCost] = useState(null);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const token = localStorage.getItem("token");

  const totalItems = items.reduce((acc, x) => acc + Number(x.quantity || 0), 0);
  const totalPrice = items.reduce(
    (acc, x) => acc + Number(x.quantity || 0) * Number(x.price || 0),
    0
  );

  const hydrateCartFromResponse = useCallback((data) => {
    const serverItems = fromApiItems(data?.items || []);
    dispatch({ type: "SET_ITEMS", payload: serverItems });

    setSubtotal(Number(data?.subtotal ?? 0));
    setTotalWeightKg(Number(data?.total_weight_kg ?? 0));
    setShippingCost(
      data?.shipping_cost !== null && data?.shipping_cost !== undefined
        ? Number(data.shipping_cost)
        : null
    );
  }, []);

  const refreshCart = useCallback(async () => {
    const tokenNow = localStorage.getItem("token");
    if (!tokenNow) return;

    try {
      const res = await api.get("/api/v1/cart");
      hydrateCartFromResponse(res.data);
    } catch (e) {
      console.error(
        "Erro a carregar carrinho do servidor:",
        e?.response || e?.message
      );
    }
  }, [hydrateCartFromResponse]);

  const resetCart = () => {
    dispatch({ type: "CLEAR" });
    setSubtotal(0);
    setTotalWeightKg(0);
    setShippingCost(null);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (!token) return;
    refreshCart();
  }, [token, refreshCart]);

  const syncLocalCartToServer = async () => {
    const tokenNow = localStorage.getItem("token");
    if (!tokenNow) return;

    const local = loadInitialCart();

    try {
      const res = await api.get("/api/v1/cart");
      const server = fromApiItems(res.data.items || []);
      const merged = mergeItems(server, local);

      await api.post("/api/v1/cart/bulk-update", {
        items: toApiItems(merged),
      });

      localStorage.removeItem(CART_STORAGE_KEY);
      await refreshCart();

      return merged;
    } catch (e) {
      console.error("Erro a sincronizar carrinho:", e?.response || e?.message);
      throw e;
    }
  };

  const upsertServer = async (variantId, quantity) => {
    const tokenNow = localStorage.getItem("token");
    if (!tokenNow) return;

    if (!variantId) {
      console.error("product_variant_id inválido:", variantId);
      return;
    }

    try {
      await api.post("/api/v1/cart/upsert-item", {
        product_variant_id: Number(variantId),
        quantity: Number(quantity),
      });

      await refreshCart();
    } catch (e) {
      console.error(
        "Erro a atualizar item no servidor:",
        e?.response?.data || e?.response || e?.message
      );
    }
  };

  const addItem = (item) => {
    dispatch({ type: "ADD_ITEM", payload: item });
   

    const existing = items.find((x) => String(x.id) === String(item.id));
    const nextQty = (existing?.quantity || 0) + (item.quantity ?? 1);
    const variantId = item.variant_id ?? item.id;

    upsertServer(variantId, nextQty);
  };

  const decrement = (id) => {
    const existing = items.find((x) => String(x.id) === String(id));
    const nextQty = (existing?.quantity || 0) - 1;
    const variantId = existing?.variant_id ?? existing?.id ?? id;

    dispatch({ type: "DECREMENT", payload: id });
    upsertServer(variantId, Math.max(0, nextQty));
  };

  const removeItem = (id) => {
    const existing = items.find((x) => String(x.id) === String(id));
    const variantId = existing?.variant_id ?? existing?.id ?? id;

    dispatch({ type: "REMOVE_ITEM", payload: id });
    upsertServer(variantId, 0);
  };

  const setQty = (id, quantity) => {
    const existing = items.find((x) => String(x.id) === String(id));
    const variantId = existing?.variant_id ?? existing?.id ?? id;

    dispatch({ type: "SET_QTY", payload: { id, quantity } });
    upsertServer(variantId, Number(quantity));
  };

  const clear = async () => {
    dispatch({ type: "CLEAR" });
    setSubtotal(0);
    setTotalWeightKg(0);
    setShippingCost(null);

    if (localStorage.getItem("token")) {
      try {
        await api.post("/api/v1/cart/clear");
      } catch (e) {
        console.error(
          "Erro a limpar carrinho no servidor:",
          e?.response || e?.message
        );
      }
    }

    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const value = {
    items,
    subtotal,
    total_weight_kg: totalWeightKg,
    shipping_cost: shippingCost,
    totalItems,
    totalPrice,
    resetCart,
    addItem,
    decrement,
    removeItem,
    setQty,
    clear,
    isCartOpen,
    openCart,
    closeCart,
    syncLocalCartToServer,
    refreshCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);

  if (!ctx) {
    throw new Error("useCart deve ser usado dentro de <CartProvider>");
  }

  return ctx;
}